import {
  Box,
  Button,
  Snackbar,
  Stack,
  ThemeProvider,
  Typography,
} from "@mui/material";
import _ from "lodash";
import { useState } from "react";
import iconChrome from "../../assets/image/googlechrome.png";
import iconSafari from "../../assets/image/safari.png";
import { theme } from "../../constants/MuiTheme";

const BLACKLIST_USER_AGENTS = [
  "line",
  "instagram",
  "fb_iab",
  "fbav",
  "fban",
  "whatsapp",
  "messenger",
  "twitter",
  "tiktok",
];
const CHROME_PLAYSTORE_URL =
  "https://play.google.com/store/apps/details?id=com.android.chrome";
const CHROME_APPSTORE_URL =
  "https://apps.apple.com/app/google-chrome/id535886823";

const SUPPORT_BROWSERS = [
  "Chrome",
  "Safari"
]

const BrowserDetection = ({ children }) => {
  const [isCopied, setCopied] = useState(false);

  const url = window.location.href;
  const userAgent = (navigator.userAgent || "").toLowerCase();
  const isInAppBrowser = detectInAppBrowser(userAgent);
  const isIOS = checkIOS(userAgent);
  const browser = detectModernBrowser(userAgent);
  const isMac = checkMac(userAgent);
  const isBrowserSupported = _.includes(SUPPORT_BROWSERS, browser);
  const isAndroid = checkAndroid(userAgent);

  console.log(
    `[BrowserDetection] isInAppBrowser=${isInAppBrowser} userAgent=${userAgent} browser=${browser} isMac=${isMac} isBrowserSupported=${isBrowserSupported}`,
  );

  const handleClick = () => {
    openChrome(url, userAgent);
  };

  const handleCloseSnackbar = () => {
    setCopied(false);
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(url);
    if (isIOS) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return isInAppBrowser || !isBrowserSupported ? (
    <ThemeProvider theme={theme}>
      <Stack
        component="main"
        height="90vh"
        spacing={2}
        padding={2}
        alignItems="center"
        justifyContent="center"
      >
        <img style={{ width: 300 }} src="../../images/logo.png" />
        <Box marginY={3}>
          <Typography variant="h5" textAlign="center">
            เพื่อประสิทธิภาพสูงสุด กรุณาเข้าใช้งานผ่าน
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
          >
            {(isIOS || isMac) && (
              <>
                <img style={{ width: 36 }} src={iconSafari} />
                <Typography variant="h5" textAlign="center">
                  Safari หรือ
                </Typography>
              </>
            )}
            <img style={{ width: 36 }} src={iconChrome} />
            <Typography variant="h5" textAlign="center">
              Google Chrome
            </Typography>
          </Stack>
        </Box>
        {(isInAppBrowser || isAndroid || isIOS) && (
          <Button
            variant="contained"
            onClick={handleClick}
            sx={{ minWidth: 140, marginBottom: 2 }}
          >
            เปิด Chrome
          </Button>
        )}
        <Button variant="outlined" onClick={handleCopy} sx={{ minWidth: 140 }}>
          คัดลอกลิงก์
        </Button>
        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          open={isCopied}
          onClose={handleCloseSnackbar}
          message="คัดลอกสำเร็จ"
        />
      </Stack>
    </ThemeProvider>
  ) : (
    children
  );
};

const checkAndroid = (userAgent = "") => {
  return /Android/i.test(userAgent);
};

const checkIOS = (userAgent = "") => {
  return /iPhone|iPad|iPod/i.test(userAgent);
};

const openChrome = (url, userAgent) => {
  let pageIsInBackground = false;

  const handleVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      pageIsInBackground = true;
    }
  };

  if (checkAndroid(userAgent)) {
    const chromeIntentUrl = `intent://${url.replace(
      /^https?:\/\//,
      "",
    )}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(
      CHROME_PLAYSTORE_URL,
    )};end`;

    window.location.href = chromeIntentUrl;
    return;
  }

  if (checkIOS(userAgent)) {
    document.addEventListener("visibilitychange", handleVisibilityChange);
    const chromeDeeplink = `googlechromes://${url.replace(/^https?:\/\//, "")}`;
    window.location.href = chromeDeeplink;

    // If Chrome not installed → fallback after delay
    setTimeout(() => {
      if (!pageIsInBackground) {
        window.location.href = CHROME_APPSTORE_URL;
      }
    }, 1500);
  }
};

const openDefaultBrowser = (url) => {
  const newUrl = new URL(url);
  newUrl.searchParams.set("openExternalBrowser", "1");
  window.location.replace(newUrl.toString());
};

const detectModernBrowser = (userAgent) => {
  if (navigator.userAgentData) {
    const brands = navigator.userAgentData.brands || [];
    for (const b of brands) {
      if (b.brand.includes("Edge")) return "Edge";
      if (b.brand.includes("Google Chrome")) return "Chrome";
    }
  }
  return detectBrowser(userAgent); // fallback to UA parsing
}

const detectInAppBrowser = (userAgent) => {
  const isInAppBrowser = _.some(BLACKLIST_USER_AGENTS, (agent) => {
    // return true;
    return userAgent.includes(agent);
  });
  return isInAppBrowser;
}

const detectBrowser = (userAgent) => {
  if (/SamsungBrowser/i.test(userAgent)) return "Samsung Internet";
  if (/MiuiBrowser/i.test(userAgent)) return "Mi Browser";
  if (/HuaweiBrowser/i.test(userAgent)) return "Huawei Browser";
  if (/OppoBrowser/i.test(userAgent)) return "Oppo Browser";
  if (/VivoBrowser/i.test(userAgent)) return "Vivo Browser";
  if (/UCBrowser/i.test(userAgent)) return "UC Browser";
  if (/Brave/i.test(userAgent)) return "Brave";
  if (userAgent.includes("edg")) return "Edge";
  if (userAgent.includes("opr")) return "Opera";
  if (userAgent.includes("firefox")) return "Firefox";
  if (userAgent.includes("chrome")) return "Chrome";
  if (userAgent.includes("safari")) return "Safari";

  return "Unknown";
}

const checkMac = (userAgent) => {
  // Modern way (some browsers reduce UA)
  if (navigator.userAgentData?.platform) {
    return navigator.userAgentData.platform === "macOS";
  }

  // Fallback
  return /Macintosh|MacIntel|MacPPC|Mac68K/i.test(userAgent);
}

export default BrowserDetection;
